import React from 'react';
import {
  Users,
  ExternalLink,
  BookOpen,
  Calendar,
  FileText,
  ArrowRight,
  Globe,
  UtensilsCrossed,
  Download,
  GraduationCap
} from 'lucide-react';

const ParentsCorner = () => {
  const externalUrl = 'https://sites.google.com/schools.nyc.gov/parentscorner/HOME';

  const docsBase = '/assets/docs/';
  const supplyDocsBase = '/assets/docs/supplylist/';

  const supplyPackets = [
    {
      grade: 'Kindergarten',
      supplyList: 'K_SupplyList.pdf',
      summerPackets: [{ label: 'Summer Packet', file: 'Kindergarten_Summer_Packet.pdf' }]
    },
    {
      grade: '1st Grade',
      supplyList: '1st_Grade_Supply_List.pdf',
      summerPackets: [{ label: 'Summer Packet', file: '1st_Grade_Summer_Packet_2026.pdf' }]
    },
    {
      grade: '2nd Grade',
      supplyList: '2nd_Grade_Supply_List_For_2026-2027.pdf',
      summerPackets: [{ label: 'Summer Assignment', file: '2nd_Grade_Summer_Assignment_2026.pdf' }]
    },
    {
      grade: '3rd Grade',
      supplyList: '3rd_Grade_Supply_List.pdf',
      summerPackets: [{ label: 'Summer Assignment', file: '3rd_Grade_Summer_Assignment_2026.pdf' }]
    },
    {
      grade: '4th Grade',
      supplyList: 'Fourth_Grade_School_Supplies_26-27.pdf',
      summerPackets: [{ label: 'Summer Packet', file: '4th_Grade_2026_Summer_Packet.pdf' }]
    },
    {
      grade: '5th & 6th Grade',
      supplyList: '5_6_Supplies_List_for_Students_2026-2027.pdf',
      summerPackets: [{ label: 'STEM Summer Packet', file: '5th_6th_STEM_Summer_Packet_2026-2027.pdf' }]
    },
    {
      grade: '7th & 8th Grade',
      supplyList: '7th_8th_Grade_2026-2027_Supply_List.pdf',
      summerPackets: [
        { label: 'ELA Summer Assignment', file: '7th_8th_Grade_ELA_Summer_Assignment_Shared.pdf' },
        { label: 'Social Studies Summer Assignment', file: '7th_8th_Grade_Social_Studies_Summer_Assignment_Shared.pdf' },
        { label: 'Science Summer Assignment', file: '7th_8th_Science_Summer_Assignment_2026.pdf' }
      ]
    }
  ];

  const resources = [
    {
      icon: FileText,
      title: 'Monthly Newsletters',
      description: 'Stay informed with our monthly updates and announcements',
      link: '/assets/docs/October_2026_Newsletter.pdf'
    },
    {
      icon: Calendar,
      title: 'Monthly Calendar',
      description: 'October 2026 school events, after-school programs, and meetings',
      link: '/assets/docs/October_2026_Calendar.pdf'
    },
    {
      icon: UtensilsCrossed,
      title: 'Meal Menus',
      description: 'View breakfast, lunch, and after-school supper menus',
      links: [
        { label: 'November 2026 High School Breakfast Menu (Halal option)', file: 'November_2026_High_School_Breakfast_Menu_Halal.pdf' },
        { label: 'November 2026 Food Court Menu (Halal option)', file: 'November_2026_Food_Court_Menu_Halal.pdf' },
        { label: 'November 2026 After School Hot Supper Menu (Halal option)', file: 'November_2026_After_School_Hot_Supper_Menu_Halal.pdf' }
      ]
    },
    {
      icon: BookOpen,
      title: 'Parent Handbook',
      description: 'Updated for the 2026-2027 school year',
      link: '/assets/docs/TAG_Parent_Handbook.pdf'
    }
  ];

  return (
    <main id="main-content" className="bg-white pt-20">
      {/* Hero Section */}
      <section className="section-padding hero-gradient text-white">
        <div className="container-padding">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center space-x-3 bg-white/10 backdrop-blur-md px-6 py-3 rounded-full border border-white/20 mb-8">
              <Users className="h-5 w-5" />
              <span className="text-sm font-medium tracking-wide uppercase">Parent Resources</span>
            </div>

            <h1 className="text-4xl md:text-6xl font-light mb-8 leading-tight">
              <span className="block font-extralight text-white/90">Lead</span>
              <span className="block font-medium">Parent's Corner</span>
            </h1>

            <p className="text-xl text-white/90 mb-12 font-light max-w-3xl mx-auto leading-relaxed">
              Your hub for important school information, resources, and community updates.
            </p>

            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-8 mb-8">
              <Globe className="h-12 w-12 mx-auto mb-4 text-gold-400" />
              <p className="text-white/90 mb-6 font-light">
                Access additional resources and information on our Parent's Corner site.
              </p>
              <a
                href={externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center bg-white text-primary-900 hover:bg-white/95 font-medium px-10 py-4 rounded-lg transition-all duration-300 shadow-lg"
              >
                Visit Parent's Corner <ExternalLink className="ml-2 h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* What You'll Find Section */}
      <section className="section-padding bg-white">
        <div className="container-padding">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-primary-100 text-primary-700 px-4 py-2 rounded-full mb-6">
              <FileText className="h-4 w-4" />
              <span className="text-sm font-medium">Available Resources</span>
            </div>

            <h2 className="text-4xl font-light text-primary-900 mb-6 leading-tight">
              What You'll Find in
              <span className="font-medium block">Parent's Corner</span>
            </h2>

            <p className="text-lg text-primary-700 font-light leading-relaxed">
              The Parent's Corner is your go-to resource for staying connected with everything happening at TAG.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {resources.map((resource, index) => {
              const Component = resource.link ? 'a' : 'div';
              const isPDF = resource.link && resource.link.endsWith('.pdf');
              const isExternal = resource.link && resource.link.startsWith('http');
              const linkProps = resource.link ? {
                href: resource.link,
                target: (isPDF || isExternal) ? '_blank' : '_self',
                rel: (isPDF || isExternal) ? 'noopener noreferrer' : undefined
              } : {};

              return (
                <Component
                  key={index}
                  {...linkProps}
                  aria-label={resource.link ? `${resource.title}${isPDF ? ' (opens PDF in a new tab)' : isExternal ? ' (opens in a new tab)' : ''}` : undefined}
                  className={`bg-neutral-50 rounded-2xl p-8 border border-neutral-200 ${resource.link ? 'card-hover cursor-pointer' : ''}`}
                >
                  <div className="w-14 h-14 bg-primary-50 rounded-lg flex items-center justify-center mb-6 border border-primary-100">
                    <resource.icon aria-hidden="true" className="h-7 w-7 text-primary-700" />
                  </div>
                  <h3 className="text-xl font-medium text-primary-900 mb-3">
                    {resource.title}
                    {isPDF && <span className="text-sm font-normal text-primary-700 ml-2">(PDF)</span>}
                  </h3>
                  <p className="text-primary-700 font-light leading-relaxed">{resource.description}</p>
                  {resource.links && (
                    <div className="flex flex-col space-y-3 mt-5">
                      {resource.links.map((doc) => (
                        <a
                          key={doc.file}
                          href={`${docsBase}${encodeURIComponent(doc.file)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`${doc.label} (opens PDF in a new tab)`}
                          className="inline-flex items-start text-primary-700 hover:text-primary-900 font-medium transition-colors"
                        >
                          <Download aria-hidden="true" className="h-4 w-4 mr-2 mt-1 flex-shrink-0" />
                          <span>{doc.label}</span>
                        </a>
                      ))}
                    </div>
                  )}
                </Component>
              );
            })}
          </div>
        </div>
      </section>

      {/* Supply Lists & Summer Work Packets */}
      <section className="section-padding bg-neutral-50">
        <div className="container-padding">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-primary-100 text-primary-700 px-4 py-2 rounded-full mb-6">
              <GraduationCap className="h-4 w-4" />
              <span className="text-sm font-medium">2026-2027 School Year</span>
            </div>

            <h2 className="text-4xl font-light text-primary-900 mb-6 leading-tight">
              Supply Lists &amp;
              <span className="font-medium block">Summer Work Packets</span>
            </h2>

            <p className="text-lg text-primary-700 font-light leading-relaxed">
              Find your child's grade below to download their supply list and any summer assignments.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {supplyPackets.map((pkg) => (
              <div
                key={pkg.grade}
                className="bg-white rounded-2xl p-8 border border-neutral-200"
              >
                <h3 className="text-xl font-medium text-primary-900 mb-5">{pkg.grade}</h3>
                <div className="flex flex-col space-y-3">
                  <a
                    href={`${supplyDocsBase}${pkg.supplyList}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${pkg.grade} Supply List (opens PDF in a new tab)`}
                    className="inline-flex items-center text-primary-700 hover:text-primary-900 font-medium transition-colors"
                  >
                    <Download aria-hidden="true" className="h-4 w-4 mr-2" />
                    Supply List
                  </a>
                  {pkg.summerPackets.map((packet) => (
                    <a
                      key={packet.file}
                      href={`${supplyDocsBase}${packet.file}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${pkg.grade} ${packet.label} (opens PDF in a new tab)`}
                      className="inline-flex items-center text-primary-700 hover:text-primary-900 font-medium transition-colors"
                    >
                      <Download aria-hidden="true" className="h-4 w-4 mr-2" />
                      {packet.label}
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Links Section */}
      <section className="section-padding bg-neutral-50">
        <div className="container-padding">
          <div className="max-w-4xl mx-auto">
            <div className="bg-primary-700 rounded-2xl p-12 text-white text-center">
              <h2 className="text-3xl font-light mb-6">Ready to Explore?</h2>
              <p className="text-xl text-white mb-8 font-light max-w-2xl mx-auto">
                Visit the Parent's Corner to access all the resources and information you need to stay engaged with your child's education.
              </p>
              <a
                href={externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center bg-white text-primary-900 hover:bg-white/95 font-medium px-10 py-4 rounded-lg transition-all duration-300 shadow-lg"
              >
                Visit Parent's Corner <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ParentsCorner;
